// Gaveta da camada: o ciclo de curadoria inteiro num lugar.
//
//   ingerir → ver feições → validar/rejeitar → promover
//
// A ordem na tela é a ordem do ciclo (princípio 7). Não há atalho de ingestão
// para publicação, e a tela não oferece nenhum.

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from "@tanstack/react-query";
import L from "leaflet";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  enviar,
  obter,
  type Camada,
  type Execucao,
  type Feicao,
  type GeometriaFeicao,
  type ListaFeicoes,
  type ResultadoDecisaoLote,
  type ResultadoLote,
  type ResultadoPromocao,
  type Versao,
} from "../api.js";
import { BotaoAcao, Carregando, Falha, Gaveta, Pilula, quando } from "../pecas/basicos.js";
import { Confirmar } from "../pecas/Confirmar.js";
import { desenharFeicao, useMapaLeaflet } from "../pecas/mapaLeaflet.js";
import { ehCampoDeTexto, useSelecao, type Selecao } from "../pecas/selecao.js";

const STATUS = ["aguardando_validacao", "validado", "rejeitado"] as const;
const ROTULO_STATUS: Record<string, string> = {
  aguardando_validacao: "aguardando validação",
  validado: "validado",
  rejeitado: "rejeitado",
};

/**
 * Tamanho da página.
 *
 * Era 200 num pedido único, e a tabela não dizia isso: quem visse sete linhas
 * concluía que a fila tinha acabado, e quem visse duzentas não sabia que havia
 * mais. Agora 100, menor de propósito — a primeira tela aparece antes, e o resto
 * vem por pedido explícito. A paginação é por CURSOR, então página tardia custa
 * o mesmo que a primeira.
 */
const PAGINA = 100;

/**
 * Acima disto, confirmar exige DIGITAR a contagem.
 *
 * Um diálogo de dois botões é clicado no automático — é o mesmo raciocínio que já
 * está em `Confirmar`, que pede o nome da coleção para despublicar. Aqui o que se
 * digita é o número, porque é o número que importa: escrever 24116 obriga a ler
 * quantas feições a ação alcança.
 */
const LOTE_QUE_EXIGE_DIGITAR = 1000;

const formatar = (n: number): string => n.toLocaleString("pt-BR");

export function GavetaCamada({
  camadaId,
  aoFechar,
  avisar,
  aoExecutar,
  aoSubir,
  aoAlterar,
}: {
  camadaId: string;
  aoFechar: () => void;
  avisar: (t: string, erro?: boolean) => void;
  aoExecutar: (id: string) => Promise<void>;
  aoSubir: (id: string, arquivo: File, categoria: string | null) => Promise<void>;
  aoAlterar: (id: string, corpo: Record<string, unknown>) => Promise<void>;
}): ReactNode {
  const qc = useQueryClient();
  const [filtro, setFiltro] = useState<string>("aguardando_validacao");
  const [uf, setUf] = useState("");
  // Abas, e não rolagem única: antes, Versões e Execuções ficavam ABAIXO de
  // uma tabela de até 200 feições — existiam e ninguém alcançava.
  const [aba, setAba] = useState<"feicoes" | "versoes" | "execucoes">("feicoes");
  // A validação em lote pede um segundo passo. É irreversível e atinge dezenas de
  // milhares de registros; disparava com um clique.
  const [confirmandoLote, setConfirmandoLote] = useState(false);
  // A decisão sobre a seleção, ou sobre uma feição. `ids` com um elemento é o
  // caso de uma só — o mesmo caminho, e portanto o mesmo registro na auditoria.
  const [decidindo, setDecidindo] = useState<{
    acao: "validar" | "rejeitar";
    ids: string[];
    rotulo: string;
  } | null>(null);
  const [mostrandoAtalhos, setMostrandoAtalhos] = useState(false);
  const arquivoRef = useRef<HTMLInputElement>(null);

  const camadas = useQuery({
    queryKey: ["camadas", null],
    queryFn: () => obter<Camada[]>("/camadas"),
  });
  const camada = (camadas.data ?? []).find((c) => c.id === camadaId);

  // Carregamento incremental por cursor. A chave inclui filtro e UF: trocar
  // qualquer um dos dois é OUTRA consulta, e reaproveitar as páginas da anterior
  // misturaria conjuntos.
  const feicoes = useInfiniteQuery({
    queryKey: ["feicoes", camadaId, filtro, uf],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const q = new URLSearchParams({ limite: String(PAGINA), status: filtro });
      if (uf) q.set("uf", uf);
      if (pageParam) q.set("cursor", pageParam);
      return obter<ListaFeicoes>(`/camadas/${camadaId}/feicoes?${q.toString()}`);
    },
    getNextPageParam: (ultima) => ultima.proximo_cursor,
  });

  // `useMemo` sobre as páginas do react-query, que são estáveis enquanto o dado
  // não muda. Sem isto, `carregadas` e `idsVisiveis` seriam arrays novos a cada
  // render, e tudo que depende da identidade deles — os retornos de chamada da
  // seleção, o efeito do cursor — recalcularia sem nada ter mudado.
  const paginas = feicoes.data?.pages;
  const carregadas = useMemo(() => (paginas ?? []).flatMap((p) => p.feicoes), [paginas]);
  const idsVisiveis = useMemo(() => carregadas.map((f) => f.id), [carregadas]);
  const selecao = useSelecao(idsVisiveis);
  // Os agregados vêm SÓ na primeira página — eles não mudam entre páginas da
  // mesma consulta, e o corte por geometria avalia `ST_GeometryType` em cada
  // linha da camada.
  const primeira = paginas?.[0];
  const totalFiltrado = primeira?.total_filtrado ?? null;
  const execucoes = useQuery({
    queryKey: ["execucoes", camadaId],
    queryFn: () => obter<Execucao[]>(`/camadas/${camadaId}/execucoes`),
  });
  const versoes = useQuery({
    queryKey: ["versoes", camadaId],
    queryFn: () => obter<Versao[]>(`/camadas/${camadaId}/versoes`),
  });

  const recarregar = (): void => {
    void qc.invalidateQueries({ queryKey: ["feicoes", camadaId] });
    void qc.invalidateQueries({ queryKey: ["camadas"] });
    void qc.invalidateQueries({ queryKey: ["versoes", camadaId] });
    void qc.invalidateQueries({ queryKey: ["execucoes", camadaId] });
  };

  const validarLote = useMutation({
    mutationFn: (motivo: string) =>
      enviar<ResultadoLote>(`/camadas/${camadaId}/validar`, {
        ...(uf ? { uf } : {}),
        ...(motivo ? { motivo } : {}),
      }),
    onSuccess: (r) => {
      setConfirmandoLote(false);
      // O que ACONTECEU, com o critério — não o que foi pedido. O servidor
      // devolve o filtro que aplicou, e é ele que vale.
      avisar(
        `${formatar(r.validadas)} feições validadas` +
          (r.filtro.uf ? ` em ${r.filtro.uf}` : " (todas as UFs)") +
          ". Registrado na auditoria.",
      );
      recarregar();
    },
    onError: (e: Error) => avisar(e.message, true),
  });

  /**
   * Decisão sobre a SELEÇÃO, otimista e com rollback.
   *
   * É o único caminho de decisão da tela — validar/rejeitar UMA feição é uma
   * seleção de um, pela mesma rota e com o mesmo registro na auditoria. A rota
   * `/{fid}/{acao}` de uma feição continua existindo no back office (curl,
   * automação), mas a tela não a usa mais: dois caminhos para a mesma decisão
   * divergiriam no que gravam.
   *
   * Otimista porque julgar mil registros é ritmo: esperar ida e volta a cada lote
   * transforma a tela numa fila. A linha muda de status na hora, e se o servidor
   * recusar o cache volta exatamente ao que era.
   *
   * O rollback guarda o estado ANTERIOR da consulta, não tenta desfazer a
   * mudança item a item — desfazer por diferença erra quando outra coisa alterou
   * o cache no meio, e a tela ficaria num estado que nunca existiu.
   */
  const decidirSelecao = useMutation({
    mutationFn: ({
      ids,
      acao,
      motivo,
    }: {
      ids: string[];
      acao: "validar" | "rejeitar";
      motivo: string;
    }) =>
      enviar<ResultadoDecisaoLote>(`/camadas/${camadaId}/feicoes/decidir`, {
        ids,
        acao,
        ...(motivo ? { motivo } : {}),
      }),
    onMutate: async ({ ids, acao, motivo }) => {
      // Cancelar antes de mexer: uma consulta em vôo terminaria DEPOIS do nosso
      // `setQueryData` e sobrescreveria a mudança otimista com o dado antigo.
      await qc.cancelQueries({ queryKey: ["feicoes", camadaId, filtro, uf] });
      const chave = ["feicoes", camadaId, filtro, uf];
      const antes = qc.getQueryData<InfiniteData<ListaFeicoes>>(chave);
      const alvo = new Set(ids);
      qc.setQueryData<InfiniteData<ListaFeicoes>>(chave, (atual) =>
        atual === undefined
          ? atual
          : {
              ...atual,
              pages: atual.pages.map((p) => ({
                ...p,
                feicoes: p.feicoes.map((f) =>
                  alvo.has(f.id)
                    ? {
                        ...f,
                        status: acao === "validar" ? "validado" : "rejeitado",
                        motivo_rejeicao: acao === "rejeitar" ? motivo : null,
                      }
                    : f,
                ),
              })),
            },
      );
      return { chave, antes };
    },
    onError: (e: Error, _vars, contexto) => {
      if (contexto?.antes) qc.setQueryData(contexto.chave, contexto.antes);
      avisar(e.message, true);
    },
    onSuccess: (r) => {
      setDecidindo(null);
      selecao.limpar();
      avisar(
        `${formatar(r.decididas)} ${r.acao === "validar" ? "validadas" : "rejeitadas"}` +
          // A divergência é dita, não escondida: id que não é desta camada ou
          // que não existe mais. "300 validadas" tendo mudado 280 é o silêncio
          // que este projeto já pagou várias vezes.
          (r.nao_encontradas > 0 ? `; ${formatar(r.nao_encontradas)} não encontradas` : "") +
          ". Registrado na auditoria.",
      );
    },
    // Sempre reconsulta: o otimismo é para a mão do curador, não para a verdade.
    onSettled: recarregar,
  });

  const totais = primeira?.totais ?? {};
  const validadas = totais.validado ?? 0;
  const pendentes = totais.aguardando_validacao ?? 0;

  // Quantas feições a validação em lote vai atingir.
  //
  // O servidor sempre age sobre `aguardando_validacao` e respeita a UF. Então o
  // alvo só é conhecido — e só coincide com o que está na tela — quando o filtro
  // de status É `aguardando_validacao`. Oferecer a ação sob outro filtro faria o
  // botão agir sobre linhas que o curador não está vendo, que é a pior forma de
  // "a ação respeita o filtro".
  const marcadas = [...selecao.marcadas];
  const foraDaLista = marcadas.length - selecao.visiveisMarcadas;

  /** Abre a confirmação de uma decisão sobre a seleção. */
  const pedirDecisao = useCallback(
    (acao: "validar" | "rejeitar", ids: string[], rotulo: string) => {
      if (ids.length > 0) setDecidindo({ acao, ids, rotulo });
    },
    [],
  );

  // --- Atalhos de teclado ---------------------------------------------------
  //
  // O tratador vive numa `ref` e o ouvinte é assinado UMA vez. É a lição que o
  // `Mapa.tsx` já pagou: retorno de chamada recriado a cada render, se entrar nas
  // dependências de um efeito que assina algo, refaz a assinatura a cada render.
  // Aqui o custo seria mais discreto que lá — nada visível quebra —, e por isso
  // pior de achar.
  const atalho = useRef<(e: KeyboardEvent) => void>(() => undefined);
  atalho.current = (e: KeyboardEvent) => {
    // Nunca dentro de um campo: digitar "V" no filtro de UF validaria uma feição.
    if (ehCampoDeTexto(e.target)) return;
    // Nem por cima de um diálogo: ali o `Enter` e o `Escape` são do diálogo, e
    // um `V` solto disparando por trás dele é a pior classe de surpresa.
    if (decidindo !== null || confirmandoLote || mostrandoAtalhos) {
      if (e.key === "?" && mostrandoAtalhos) setMostrandoAtalhos(false);
      return;
    }
    if (aba !== "feicoes") return;
    // Combinação com modificador é do navegador (Cmd+R recarrega, Ctrl+V cola).
    if (e.metaKey || e.ctrlKey || e.altKey) return;

    const emFoco = selecao.foco >= 0 ? carregadas[selecao.foco] : undefined;
    // A seleção tem precedência sobre o cursor: se há coisa marcada, V e R
    // falam dela. É o que a barra de ação anuncia, e duas regras diferentes para
    // a mesma tecla seriam pior que nenhuma.
    const alvo = marcadas.length > 0 ? marcadas : emFoco ? [emFoco.id] : [];
    const rotulo =
      marcadas.length > 0
        ? `${formatar(marcadas.length)} selecionadas`
        : (emFoco?.codigo_externo ?? "a feição em foco");

    switch (e.key) {
      case "j":
      case "J":
      case "ArrowDown":
        e.preventDefault();
        selecao.mover(1);
        return;
      case "k":
      case "K":
      case "ArrowUp":
        e.preventDefault();
        selecao.mover(-1);
        return;
      case "x":
      case "X":
        if (emFoco) {
          e.preventDefault();
          selecao.alternar(emFoco.id);
        }
        return;
      case "v":
      case "V":
        if (alvo.length > 0) pedirDecisao("validar", alvo, rotulo);
        return;
      case "r":
      case "R":
        if (alvo.length > 0) pedirDecisao("rejeitar", alvo, rotulo);
        return;
      case "?":
        setMostrandoAtalhos(true);
        return;
    }
  };

  useEffect(() => {
    const ouvir = (e: KeyboardEvent): void => atalho.current(e);
    document.addEventListener("keydown", ouvir);
    return () => document.removeEventListener("keydown", ouvir);
  }, []);

  /**
   * `Escape` com seleção ativa LIMPA a seleção, e não fecha a gaveta.
   *
   * Fechar e perder uma seleção de 300 feições por um toque de tecla faz a pessoa
   * refazer meia hora de trabalho. O segundo toque fecha. O botão "Esc" e o
   * clique no fundo continuam fechando direto — quem clica em fechar está pedindo
   * para fechar.
   */
  const aoEscape = useCallback((): boolean => {
    if (mostrandoAtalhos) {
      setMostrandoAtalhos(false);
      return true;
    }
    // Com diálogo aberto, o `Escape` é dele; a gaveta não se mete.
    if (decidindo !== null || confirmandoLote) {
      setDecidindo(null);
      setConfirmandoLote(false);
      return true;
    }
    if (selecao.marcadas.size > 0) {
      selecao.limpar();
      return true;
    }
    return false;
  }, [mostrandoAtalhos, decidindo, confirmandoLote, selecao]);

  const alvoDoLote = filtro === "aguardando_validacao" ? (totalFiltrado ?? 0) : 0;
  const impedimentoDoLote: string | null =
    filtro !== "aguardando_validacao"
      ? `A validação em lote age sobre o que está aguardando. Troque o filtro para "aguardando validação" — assim o que você valida é o que está na tela.`
      : pendentes === 0
        ? "Nenhuma feição aguardando validação nesta camada."
        : alvoDoLote === 0
          ? `Nenhuma pendente em ${uf}. A camada tem ${formatar(pendentes)} aguardando em outras UFs.`
          : null;

  // Fonte que declara dado pessoal exige DECISÃO antes de promover.
  //
  // `null` é "ninguém decidiu"; `[]` é "decidiu que não há o que suprimir". A
  // diferença é o que o banco usa para recusar a publicação — e recusar lá é
  // seguro e tarde: o curador já promoveu, já esperou, e descobre no fim.
  const precisaDecidirSupressao =
    (camada?.fonte_dado_pessoal ?? false) && (camada?.atributos_suprimidos ?? null) === null;

  // O guard fica DEPOIS de todos os hooks, não antes: sair mais cedo pularia os
  // `useState`/`useEffect`/`useCallback` acima, e a contagem de hooks mudaria
  // entre o render "sem camada ainda" e o "com camada" — o React aborta a
  // árvore com "Rendered more hooks than during the previous render". A regra é
  // que a lista de hooks seja idêntica em todo render; o retorno condicional só
  // é seguro abaixo dela.
  if (!camada)
    return (
      <Gaveta titulo="Camada" aoFechar={aoFechar}>
        <Carregando />
      </Gaveta>
    );

  return (
    <Gaveta
      titulo={camada.nome}
      subtitulo={`${camada.fonte_orgao} · ${camada.tipo_conector}`}
      aoFechar={aoFechar}
      aoEscape={aoEscape}
      cabecalho={
        <>
          {!camada.fonte_publicavel ? (
            <div className="aviso" style={{ marginTop: 14 }} role="status">
              <div>
                <b style={{ color: "var(--agd)" }}>Fonte sem licença ou atribuição</b>
                <p>
                  Dá para curar, não dá para publicar. A licença se registra na tela de Fontes — é o
                  que separa consultar dado de terceiro de redistribuí-lo.
                </p>
              </div>
            </div>
          ) : null}

          <div className="linha-botoes" style={{ marginTop: 16 }}>
            <BotaoAcao
              className="btn"
              rotuloCarregando="Ingerindo…"
              desabilitado={
                camada.tipo_conector === "upload_arquivo" || camada.tipo_conector === "carga_massa"
              }
              aoClicar={async () => {
                await aoExecutar(camadaId);
                recarregar();
              }}
            >
              Executar ingestão
            </BotaoAcao>

            <button className="btn" onClick={() => arquivoRef.current?.click()}>
              Subir arquivo
            </button>
            <input
              ref={arquivoRef}
              type="file"
              accept=".zip,.geojson,.json"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                void aoSubir(camadaId, f, null)
                  .then(recarregar)
                  .catch((err: Error) => avisar(err.message, true));
                e.target.value = "";
              }}
            />

            <BotaoAcao
              className="btn"
              aoClicar={async () => {
                await aoAlterar(camadaId, { ativo: !camada.ativo });
              }}
            >
              {camada.ativo ? "Desativar" : "Ativar"}
            </BotaoAcao>

            {/* A decisão de supressão, onde ela é tomada: ao lado do botão que
                promove. Guardá-la numa tela de configuração faria com que quem
                promove não a visse — e a promoção é a assinatura (ADR-010). */}
            {precisaDecidirSupressao ? (
              <BotaoAcao
                className="btn"
                rotuloCarregando="Salvando…"
                aoClicar={async () => {
                  const bruto = window.prompt(
                    "Esta fonte declara dado pessoal. Quais atributos são suprimidos na publicação?\n\n" +
                      "Separe por vírgula. Deixe em branco para declarar que NÃO há o que suprimir " +
                      "— e isso é uma decisão registrada, não o padrão.",
                    "",
                  );
                  // Cancelar é diferente de confirmar em branco: um não decide
                  // nada, o outro decide que não há o que suprimir.
                  if (bruto === null) return;
                  const lista = bruto
                    .split(",")
                    .map((x) => x.trim())
                    .filter((x) => x !== "");
                  await aoAlterar(camadaId, { atributos_suprimidos: lista });
                  avisar(
                    lista.length === 0
                      ? "Registrado: nada a suprimir nesta camada."
                      : `Suprimindo ${lista.join(", ")} em toda promoção desta camada.`,
                  );
                }}
              >
                Decidir supressão…
              </BotaoAcao>
            ) : null}

            {/* Promover é o fim do ciclo, e só faz sentido com feição
                validada. Desabilitado com o motivo à vista vale mais que um
                409 depois — e o mesmo vale para a decisão de supressão: o banco
                recusa a publicação depois, o que é seguro e tarde. */}
            <BotaoAcao
              className="btn btn-primario"
              rotuloCarregando="Promovendo…"
              desabilitado={validadas === 0 || precisaDecidirSupressao}
              aoClicar={async () => {
                try {
                  const r = await enviar<ResultadoPromocao>(`/camadas/${camadaId}/promover`, {
                    por: "curador",
                  });
                  avisar(`Versão ${r.numero} selada com ${r.feicoes} feições.`);
                  recarregar();
                } catch (e) {
                  avisar((e as Error).message, true);
                }
              }}
              style={{ marginLeft: "auto" }}
            >
              Promover versão ({validadas})
            </BotaoAcao>
          </div>

          <div className="abas" role="tablist">
            {/* O contador da aba Feições é o total FILTRADO — o mesmo número do
                cabeçalho da tabela. Era a soma de todos os status, e isso fazia
                a aba dizer 24.116 enquanto a tabela mostrava 7 registros de um
                filtro por UF: dois números sobre a mesma coisa, discordando. */}
            {(
              [
                ["feicoes", "Feições", totalFiltrado],
                ["versoes", "Versões", versoes.data?.length ?? 0],
                ["execucoes", "Execuções", execucoes.data?.length ?? 0],
              ] as const
            ).map(([chave, rotulo, n]) => (
              <button
                key={chave}
                className="aba"
                role="tab"
                aria-selected={aba === chave}
                onClick={() => setAba(chave)}
              >
                {rotulo}
                <span className="mono">{n === null ? "—" : formatar(n)}</span>
              </button>
            ))}
          </div>
        </>
      }
    >
      {aba === "feicoes" ? (
        <>
          <div className="filtros">
            {STATUS.map((x) => (
              <button
                key={x}
                className="chip"
                aria-pressed={filtro === x}
                onClick={() => setFiltro(x)}
              >
                {ROTULO_STATUS[x]}{" "}
                <span className="mono" style={{ opacity: 0.75 }}>
                  {totais[x] ?? 0}
                </span>
              </button>
            ))}
            <input
              className="chip mono"
              style={{ width: 74, marginLeft: "auto" }}
              placeholder="UF"
              aria-label="Filtrar por UF"
              maxLength={2}
              value={uf}
              onChange={(e) => setUf(e.target.value.toUpperCase())}
            />
          </div>

          {/* O botão fica SEMPRE visível, e desabilitado com o motivo ao lado.
              Antes ele desaparecia quando não havia pendentes ou quando o filtro
              era outro, e botão que não está lá não ensina nada: quem procura a
              validação em lote e não a encontra conclui que ela não existe. */}
          <div className="acao-lote">
            <BotaoAcao
              className="btn"
              rotuloCarregando="Validando…"
              desabilitado={impedimentoDoLote !== null}
              style={{ width: "100%" }}
              // Um clique abre a CONFIRMAÇÃO, não executa. A ação atinge dezenas
              // de milhares de registros e não tem desfazer.
              aoClicar={async () => setConfirmandoLote(true)}
            >
              Validar todas as pendentes{uf ? ` de ${uf}` : ""} ({formatar(alvoDoLote)})
            </BotaoAcao>
            {impedimentoDoLote ? <p className="campo-ajuda">{impedimentoDoLote}</p> : null}
          </div>

          {feicoes.isLoading ? <Carregando /> : null}
          {feicoes.error ? <Falha erro={feicoes.error} /> : null}
          {primeira ? (
            <>
              {/* Quantas estão na tela, de quantas existem sob ESTE filtro.
                  Antes a consulta cortava em 200 e a tabela não dizia nada: sete
                  linhas se leem como "a fila acabou", e duzentas como "é isso".
                  O denominador é o total filtrado, não o da camada — com `uf=PR`
                  os dois divergem, e mostrar o da camada faria "7 de 24.116"
                  onde são 7 de 7. */}
              <p className="discreto linha-contagem">
                Exibindo <b>{formatar(carregadas.length)}</b> de{" "}
                <b>{formatar(totalFiltrado ?? carregadas.length)}</b> feições{" "}
                {ROTULO_STATUS[filtro] ?? filtro}
                {uf ? ` em ${uf}` : ""}.
              </p>

              {/* A barra de ação aparece com a seleção e some quando ela zera.
                  Fica ACIMA da tabela, ao lado da contagem: uma barra flutuante
                  no rodapé cobriria as últimas linhas, que são justamente as que
                  o curador acabou de marcar. */}
              {marcadas.length > 0 ? (
                <div className="barra-selecao" role="status">
                  <span className="selecao-n">
                    <b>{formatar(marcadas.length)}</b> selecionadas
                    {/* A seleção sobrevive à troca de filtro e de página, e o
                        que está fora da lista atual é DITO. Sem esta contagem,
                        preservar a seleção seria esconder parte dela. */}
                    {foraDaLista > 0 ? (
                      <span className="terciario"> · {formatar(foraDaLista)} fora desta lista</span>
                    ) : null}
                  </span>
                  <button
                    className="btn btn-pequeno"
                    onClick={() =>
                      pedirDecisao("validar", marcadas, `${formatar(marcadas.length)} selecionadas`)
                    }
                  >
                    Validar
                  </button>
                  <button
                    className="btn btn-pequeno btn-perigo"
                    onClick={() =>
                      pedirDecisao(
                        "rejeitar",
                        marcadas,
                        `${formatar(marcadas.length)} selecionadas`,
                      )
                    }
                  >
                    Rejeitar
                  </button>
                  <button className="btn btn-pequeno" onClick={selecao.limpar}>
                    Limpar
                  </button>
                  <button
                    className="link-texto"
                    onClick={() => setMostrandoAtalhos(true)}
                    aria-label="Atalhos de teclado"
                    title="Atalhos de teclado"
                  >
                    ?
                  </button>
                </div>
              ) : (
                <p className="discreto dica-atalhos">
                  Marque linhas para decidir em lote.{" "}
                  <button className="link-texto" onClick={() => setMostrandoAtalhos(true)}>
                    Atalhos de teclado (?)
                  </button>
                </p>
              )}

              <TabelaFeicoes
                camadaId={camadaId}
                feicoes={carregadas}
                selecao={selecao}
                aoDecidir={(fid, acao) =>
                  pedirDecisao(
                    acao as "validar" | "rejeitar",
                    [fid],
                    carregadas.find((x) => x.id === fid)?.codigo_externo ?? "feição sem código",
                  )
                }
                aoRejeitarUma={(f) =>
                  pedirDecisao("rejeitar", [f.id], f.codigo_externo ?? "feição sem código")
                }
              />

              {/* Carregamento incremental, e não rolagem infinita: a tela é de
                  julgamento, e o curador precisa poder parar onde parou. Uma
                  lista que cresce sozinha ao rolar perde o lugar dele. */}
              {feicoes.hasNextPage ? (
                <BotaoAcao
                  className="btn"
                  rotuloCarregando="Carregando…"
                  style={{ width: "100%", marginTop: 12 }}
                  aoClicar={async () => {
                    await feicoes.fetchNextPage();
                  }}
                >
                  Carregar mais {formatar(PAGINA)}
                  {totalFiltrado !== null
                    ? ` — faltam ${formatar(Math.max(0, totalFiltrado - carregadas.length))}`
                    : ""}
                </BotaoAcao>
              ) : carregadas.length > PAGINA ? (
                <p className="discreto" style={{ marginTop: 12, textAlign: "center" }}>
                  Fim da lista.
                </p>
              ) : null}
            </>
          ) : null}
        </>
      ) : null}

      {aba === "versoes" ? (
        (versoes.data ?? []).length === 0 ? (
          <p className="vazio">Nenhuma versão promovida.</p>
        ) : (
          <div className="rolagem">
            <table className="tabela">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Feições</th>
                  <th>Selada</th>
                  <th>Por</th>
                </tr>
              </thead>
              <tbody>
                {(versoes.data ?? []).map((v) => (
                  <tr key={v.id}>
                    <td className="mono" style={{ color: "var(--ac)" }}>
                      {v.numero}
                    </td>
                    <td className="mono">{v.total_feicoes}</td>
                    <td className="mono discreto">{quando(v.selada_em)}</td>
                    <td>{v.promovida_por}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}

      {mostrandoAtalhos ? <AjudaAtalhos aoFechar={() => setMostrandoAtalhos(false)} /> : null}

      {decidindo ? (
        <Confirmar
          titulo={decidindo.acao === "validar" ? "Validar seleção" : "Rejeitar seleção"}
          rotuloAcao={`${decidindo.acao === "validar" ? "Validar" : "Rejeitar"} ${formatar(decidindo.ids.length)}`}
          perigo={decidindo.acao === "rejeitar"}
          {...(decidindo.ids.length > LOTE_QUE_EXIGE_DIGITAR
            ? { confirmacao: String(decidindo.ids.length) }
            : {})}
          // Rejeitar EXIGE motivo, e o servidor recusa sem ele. A tela pede antes
          // para a recusa não chegar como erro depois de a pessoa confirmar.
          {...(decidindo.acao === "rejeitar" ? { motivoObrigatorio: true } : {})}
          descricao={
            <>
              <p>
                <b>{formatar(decidindo.ids.length)}</b>{" "}
                {decidindo.ids.length === 1 ? "feição passa" : "feições passam"} a{" "}
                <b>{decidindo.acao === "validar" ? "validado" : "rejeitado"}</b> em{" "}
                <b>{camada.nome}</b>.
              </p>
              <p>{decidindo.rotulo}.</p>
              {foraDaLista > 0 && decidindo.ids.length === marcadas.length ? (
                <p>
                  <b>{formatar(foraDaLista)}</b> delas não estão na lista atual — a seleção
                  sobreviveu à troca de filtro ou de página, de propósito.
                </p>
              ) : null}
              <p>
                <b>Não tem desfazer.</b> Reverter é decidir de novo, feição a feição.
              </p>
            </>
          }
          aoConfirmar={(motivo) =>
            decidirSelecao.mutate({ ids: decidindo.ids, acao: decidindo.acao, motivo })
          }
          aoCancelar={() => setDecidindo(null)}
          pendente={decidirSelecao.isPending}
        />
      ) : null}

      {confirmandoLote ? (
        <Confirmar
          titulo="Validar em lote"
          rotuloAcao={`Validar ${formatar(alvoDoLote)}`}
          // Acima de mil, exige DIGITAR a contagem. Um "OK" com dois botões é
          // clicado no automático; escrever 24116 exige ler o número que está
          // escrito acima. O limite é o do item 6 da revisão.
          {...(alvoDoLote > LOTE_QUE_EXIGE_DIGITAR ? { confirmacao: String(alvoDoLote) } : {})}
          descricao={
            <>
              <p>
                <b>{formatar(alvoDoLote)} feições</b> passam a <b>validado</b> em{" "}
                <b>{camada.nome}</b>.
              </p>
              {/* O filtro ATIVO, escrito. A ação respeita o filtro e é fácil
                  esquecer disso — um curador que filtrou por UF meia hora antes
                  não lembra, e a diferença entre validar 1.200 e 24.116 é essa
                  linha. */}
              <p>
                Filtro ativo: status <code className="mono">aguardando validação</code>
                {uf ? (
                  <>
                    , UF <code className="mono">{uf}</code>
                  </>
                ) : (
                  ", todas as UFs"
                )}
                .
              </p>
              <p>
                <b>Não tem desfazer.</b> O caminho de volta é rejeitar feição a feição. Promover
                depois disso sela uma versão imutável com o que foi validado.
              </p>
            </>
          }
          aoConfirmar={(motivo) => validarLote.mutate(motivo)}
          aoCancelar={() => setConfirmandoLote(false)}
          pendente={validarLote.isPending}
        />
      ) : null}

      {aba === "execucoes" ? (
        (execucoes.data ?? []).length === 0 ? (
          <p className="vazio">Nenhuma execução registrada.</p>
        ) : (
          <div className="rolagem">
            <table className="tabela">
              <thead>
                <tr>
                  <th>Quando</th>
                  <th>Status</th>
                  <th>Registros</th>
                  <th>Mensagem</th>
                </tr>
              </thead>
              <tbody>
                {(execucoes.data ?? []).slice(0, 30).map((e) => (
                  <tr key={e.id}>
                    <td className="mono discreto" style={{ whiteSpace: "nowrap" }}>
                      {quando(e.executado_em)}
                    </td>
                    <td>
                      <Pilula status={e.status} />
                    </td>
                    <td className="mono">{e.registros_processados}</td>
                    <td className="discreto">{e.mensagem ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}
    </Gaveta>
  );
}

function TabelaFeicoes({
  camadaId,
  feicoes,
  selecao,
  aoDecidir,
  aoRejeitarUma,
}: {
  camadaId: string;
  feicoes: Feicao[];
  selecao: Selecao;
  aoDecidir: (fid: string, acao: string, motivo?: string | undefined) => void;
  aoRejeitarUma: (f: Feicao) => void;
}): ReactNode {
  // UMA linha expandida por vez: o mini-mapa é caro (monta um Leaflet), e dois
  // ou três abertos ao mesmo tempo competiriam por espaço e por atenção. Abrir
  // outra recolhe a anterior.
  const [expandida, setExpandida] = useState<string | null>(null);
  // As linhas são focáveis, e o foco é movido de fato — não simulado com uma
  // classe. É o que faz o contorno de `:focus-visible` aparecer onde o cursor
  // está e o que permite a um leitor de tela anunciar a linha.
  const linhas = useRef(new Map<string, HTMLTableRowElement>());
  const comFoco = selecao.foco >= 0 ? feicoes[selecao.foco]?.id : undefined;

  useEffect(() => {
    if (comFoco === undefined) return;
    const no = linhas.current.get(comFoco);
    // `preventScroll` NÃO: mover o cursor para uma linha fora da vista e não
    // rolar até ela é pior que não mover.
    no?.focus();
  }, [comFoco]);

  if (feicoes.length === 0) return <p className="vazio">Nenhuma feição neste filtro.</p>;

  return (
    <div className="rolagem">
      <table className="tabela tabela-selecionavel">
        <thead>
          <tr>
            <th className="col-marca">
              <input
                type="checkbox"
                // Marca a PÁGINA VISÍVEL, não a camada. Um checkbox de cabeçalho
                // que selecionasse 24 mil registros invisíveis seria a mesma
                // armadilha da validação em lote, sem nem o aviso.
                aria-label="Marcar as feições desta lista"
                checked={selecao.paginaInteira}
                ref={(no) => {
                  // Terceiro estado do checkbox nativo: parte da página marcada
                  // não é "marcado" nem "desmarcado", e só existe por
                  // propriedade — não há atributo HTML para isso.
                  if (no) no.indeterminate = selecao.paginaParcial;
                }}
                onChange={selecao.alternarPagina}
              />
            </th>
            <th>Código</th>
            <th>UF</th>
            <th>Atributos</th>
            <th>Geometria</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {feicoes.map((f, i) => {
            const marcada = selecao.marcadas.has(f.id);
            const aberta = expandida === f.id;
            return (
              <Fragment key={f.id}>
                <tr
                  ref={(no) => {
                    if (no) linhas.current.set(f.id, no);
                    else linhas.current.delete(f.id);
                  }}
                  tabIndex={-1}
                  aria-selected={marcada}
                  className={`${marcada ? "linha-marcada" : ""} ${selecao.foco === i ? "linha-com-cursor" : ""}`}
                  onFocus={() => selecao.focar(i)}
                >
                  <td className="col-marca">
                    <input
                      type="checkbox"
                      checked={marcada}
                      aria-label={`Marcar ${f.codigo_externo ?? "feição sem código"}`}
                      // `onClick` e não `onChange` para ler o Shift: o evento de
                      // mudança não carrega os modificadores de teclado.
                      onClick={(e) => {
                        selecao.alternar(f.id, e.shiftKey);
                        // Shift-clique dentro de uma tabela seleciona TEXTO por
                        // padrão do navegador, e o resultado é a linha inteira
                        // realçada em azul por cima do nosso realce.
                        if (e.shiftKey) document.getSelection()?.removeAllRanges();
                      }}
                      onChange={() => undefined}
                    />
                  </td>
                  <td>
                    {/* O código expande a linha — clicar abre o mini-mapa. É o
                        gesto de "quero VER esta", separado do checkbox, que é
                        "quero DECIDIR sobre esta". */}
                    <button
                      className="expandir"
                      aria-expanded={aberta}
                      onClick={() => setExpandida(aberta ? null : f.id)}
                    >
                      <span className="expandir-seta" aria-hidden="true">
                        {aberta ? "▾" : "▸"}
                      </span>
                      <span className="mono">{f.codigo_externo ?? "—"}</span>
                    </button>
                  </td>
                  <td className="mono">{f.uf ?? "—"}</td>
                  <td className="discreto">{resumoAtributos(f.atributos)}</td>
                  <td>
                    {/* Feição sem geometria não desenha no mapa. O dado bruto foi
                    preservado, mas o curador precisa saber que este registro não
                    aparece em lugar nenhum. */}
                    {f.tem_geometria ? (
                      <span className="mono discreto">{f.tipo_geometria ?? "sim"}</span>
                    ) : (
                      <span className="pilula pilula-erro">sem geometria</span>
                    )}
                  </td>
                  <td>
                    {/* Os botões de decidir são SÓ para a feição que ainda espera.
                      Validado e rejeitado já foram decididos — mostrá-los com
                      "Validar/Rejeitar" convidaria a decidir de novo o que já
                      está decidido, e é isso que torna a mudança otimista
                      visível: a linha perde os botões na hora. */}
                    {f.status === "aguardando_validacao" ? (
                      <span style={{ display: "flex", gap: 6 }}>
                        <button
                          className="btn btn-pequeno"
                          onClick={() => aoDecidir(f.id, "validar")}
                        >
                          Validar
                        </button>
                        <button
                          className="btn btn-pequeno btn-perigo"
                          onClick={() => aoRejeitarUma(f)}
                        >
                          Rejeitar
                        </button>
                      </span>
                    ) : (
                      <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <Pilula status={f.status} />
                        {f.status === "rejeitado" && f.motivo_rejeicao ? (
                          <span className="discreto">{f.motivo_rejeicao}</span>
                        ) : null}
                      </span>
                    )}
                  </td>
                </tr>
                {aberta ? (
                  <tr className="linha-expandida">
                    {/* colSpan cobre as cinco colunas + a de marca. */}
                    <td colSpan={6}>
                      <PainelFeicao camadaId={camadaId} feicao={f} />
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * O painel da linha expandida: mini-mapa, atributos completos.
 *
 * A geometria é buscada AQUI, ao expandir — a lista não a traz, e são milhares de
 * linhas. `enabled` só dispara a consulta quando o painel existe, e o cache do
 * react-query guarda a resposta se a linha for reaberta.
 *
 * O mini-mapa reusa `useMapaLeaflet` — a MESMA montagem da tela cheia, não uma
 * cópia. É o que garante que a correção do bug de recriação valha aqui também.
 */
function PainelFeicao({ camadaId, feicao }: { camadaId: string; feicao: Feicao }): ReactNode {
  const q = useQuery({
    queryKey: ["geometria", camadaId, feicao.id],
    queryFn: () => obter<GeometriaFeicao>(`/camadas/${camadaId}/feicoes/${feicao.id}/geometria`),
  });

  // Mini-mapa NÃO interativo: é para ver, não navegar. `interativo: false`
  // desliga arrasto, zoom e o ponto de tabulação por linha.
  const { divRef, mapa, geracao, basemap } = useMapaLeaflet({ interativo: false });
  const camadaRef = useRef<L.GeoJSON | null>(null);

  const geometria = q.data?.geometria ?? null;
  useEffect(() => {
    void geracao;
    if (!mapa) return;
    // Redesenha do zero a cada mudança: remove a anterior antes de pôr a nova.
    if (camadaRef.current) {
      camadaRef.current.remove();
      camadaRef.current = null;
    }
    if (geometria) {
      camadaRef.current = desenharFeicao(mapa, geometria, {
        cor: feicao.status === "validado" ? "#7fa9ff" : "#e3a93b",
        enquadrar: true,
      });
    }
  }, [mapa, geracao, geometria, feicao.status]);

  return (
    <div className="painel-feicao">
      <div className="painel-feicao-mapa">
        {q.isLoading ? (
          <div className="mini-mapa-vazio">Carregando geometria…</div>
        ) : q.error ? (
          <Falha erro={q.error} />
        ) : geometria === null ? (
          // Geometria ausente NÃO é erro: a feição existe e o dado bruto foi
          // preservado. Ela só não desenha, e o curador precisa saber disso —
          // é justamente o registro que some do mapa.
          <div className="mini-mapa-vazio">
            Esta feição não tem geometria aproveitável. Não desenha no mapa.
          </div>
        ) : (
          // Sem a classe `tema-escuro` explícita: o mini-mapa herda o tema do
          // documento por um seletor que olha `:root:not([data-tema="claro"])`.
          // A gaveta não recebe `aparencia`, e ler o atributo aqui não seria
          // reativo à troca de tema — o CSS resolve sem estado. `inverte-base`
          // vem do mapa base que o hook usa, e não da suposição de que o padrão
          // é um mapa desenhado: satélite invertido vira negativo.
          <div
            className={`mapa-area mapa-area-mini${basemap.inverteNoEscuro ? " inverte-base" : ""}`}
          >
            <div ref={divRef} className="mapa-tela" />
          </div>
        )}
      </div>
      <div className="painel-feicao-atributos">
        <div className="legenda-titulo">Atributos</div>
        {Object.keys(feicao.atributos).length === 0 ? (
          <p className="discreto">Sem atributos.</p>
        ) : (
          <dl className="atributos-completos">
            {Object.entries(feicao.atributos).map(([k, v]) => (
              <div key={k}>
                <dt className="mono">{k}</dt>
                <dd className="mono">{String(v)}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </div>
  );
}

/** A lista de atalhos, na própria tela. Atalho que ninguém descobre não existe. */
function AjudaAtalhos({ aoFechar }: { aoFechar: () => void }): ReactNode {
  return (
    <div className="modal-fundo" role="dialog" aria-modal="true" aria-label="Atalhos de teclado">
      <div className="modal">
        <h3>Atalhos de teclado</h3>
        <div className="modal-corpo">
          <dl className="lista-atalhos">
            {(
              [
                ["J", "ou ↓ — desce uma linha"],
                ["K", "ou ↑ — sobe uma linha"],
                ["X", "marca ou desmarca a linha"],
                ["Shift + clique", "marca o intervalo até a última marcada"],
                ["V", "valida a seleção, ou a linha em foco"],
                ["R", "rejeita a seleção, ou a linha em foco"],
                ["Esc", "limpa a seleção; sem seleção, fecha a gaveta"],
                ["?", "abre e fecha esta lista"],
              ] as const
            ).map(([tecla, o_que]) => (
              <div key={tecla}>
                <dt>
                  <kbd>{tecla}</kbd>
                </dt>
                <dd>{o_que}</dd>
              </div>
            ))}
          </dl>
          <p>
            Nenhum deles dispara com o cursor dentro de um campo — digitar uma UF no filtro não
            valida nada.
          </p>
        </div>
        <div className="linha-botoes">
          <button className="btn btn-primario" onClick={aoFechar} autoFocus>
            Entendi
          </button>
        </div>
      </div>
    </div>
  );
}

/** Três atributos bastam para reconhecer o registro; o resto é ruído na tabela. */
function resumoAtributos(a: Record<string, unknown>): string {
  const partes = Object.entries(a)
    .slice(0, 3)
    .map(([k, v]) => `${k}=${String(v).slice(0, 28)}`);
  const sobra = Object.keys(a).length - partes.length;
  return partes.join(" · ") + (sobra > 0 ? ` · +${sobra}` : "") || "—";
}
